import { type CSSProperties, type FC, type ReactNode, useEffect } from 'react';

import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';

import { connectEruda, disconnectEruda } from '@/shared/helpers/connect-eruda';
import { Lottie } from '@/shared/hocs/lottieWrapper';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';

import duckLottie from '@/assets/lotties/duck.lottie?url';

import { ColorVariables } from '@/tokens';

interface Props {
  className?: string;
  title?: ReactNode;
  message?: ReactNode;
  buttonLabel?: ReactNode;
  onClickButton?: () => void;

  isReload?: boolean;
}

interface Styles {
  container: CSSProperties;
  title: CSSProperties;
  message: CSSProperties;
  link: CSSProperties;
  lottie: CSSProperties;
}

const FONT_VARIANTS = `
  ui-rounded,
  -apple-system,
  BlinkMacSystemFont,
  'Segoe UI',
  Roboto,
  Oxygen,
  Ubuntu,
  Cantarell,
  'Open Sans',
  'Helvetica Neue',
  sans-serif
`;

const getStyles = (): Styles => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    width: '100vw',
    height: '100vh',
    padding: '40px 28px',
    gap: '8px',
    overflow: 'auto',
    backgroundColor: WebApp?.themeParams?.bg_color || ColorVariables.BG_COLOR,
  },
  title: {
    color: WebApp?.themeParams?.text_color || ColorVariables.TEXT_COLOR,
    textAlign: 'center',
    fontSize: '22px',
    fontWeight: 600,
    lineHeight: '28px',
    letterSpacing: 0,
    fontFamily: FONT_VARIANTS,
  },
  message: {
    color: WebApp?.themeParams?.hint_color || ColorVariables.HINT_COLOR,
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '19px',
    letterSpacing: -0.23,
    marginTop: 10,
  },
  link: {
    color: WebApp?.themeParams?.link_color || ColorVariables.LINK_COLOR,
    textAlign: 'center',
    marginTop: '22px',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: 0,
    fontFamily: FONT_VARIANTS,
  },
  lottie: { height: '112px', width: '112px' },
});

export const ErrorPage: FC<Props> = ({
  title,
  message,
  buttonLabel,
  onClickButton = () => (window.location.href = '/'),

  isReload,
}) => {
  const { t } = useTranslation();
  const { isKassir } = useFeatureFlag();

  const styles = getStyles();

  useEffect(() => {
    if (isKassir) connectEruda();

    return () => disconnectEruda();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={styles.container}>
      <Lottie src={duckLottie} style={styles.lottie} loop autoplay />

      <div style={styles.title}>{title ?? t('Something went wrong')}</div>
      {!!message && <div style={styles.message}>{message}</div>}

      <a onClick={onClickButton} style={styles.link}>
        {isReload ? (buttonLabel ?? t('Go to Games')) : t('Reload Page')}
      </a>
    </div>
  );
};
